import * as React from 'react';

import { Box, Typography, Button, Divider, IconButton, Tooltip, Tabs, Tab, FormControlLabel, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { loadOpenedModel, loadOpenedModelByName } from '../../sagas/dataSaga/dataSagaActions';
import { getIsLoadingOpenedModel, getIsModelDuplicatedTo, getIsModelWasRemoved, getOpenedModel, setIsModelDuplicatedTo, setIsModelWasRemoved } from '../../reducers/data/dataReducer';
import { useDispatch, useSelector } from 'react-redux';
import { editTrainedModelModalAction, openDuplicateModelModalAction, openOnnxModalAction, openRunModelModalAction, removeTrainedModelAction } from '../../sagas/trainedModelSaga/trainedModelSagaActions';
import { AITrainModelQueueStateTypeEnum, AITrainModelStateTypeEnum } from '../../@shared/Models/Models';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Sizes, Palette } from '../../@shared/palette';
import { FlexBoxInline, PageHeaderSx, SecondaryBtnSx, PageDescriptionSx, ThirdBtnSx, RemoveBtnSx, TabSx, BadgeBoxSx, PrimaryBtnSx, TooltipSlotProps, LinkSxSmall } from '../../@shared/templates';
import TabsContent from './TabsContent';
import { useBadge } from '../../hooks/useBadge';
import { useModel } from '../../hooks/useModel';
import ModalShimmer from './ModalShimmer';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import CalculateIcon from '@mui/icons-material/Calculate';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CancelIcon from '@mui/icons-material/Cancel'; 
import { ModelType } from '../../@shared/Models/ModelTypes';
import GuideCallout from '../../components/GuideCallout';
import { getGuideStep, setGuideStep } from '../../reducers/main/mainReducer';
import { GuideStepEnum } from '../../reducers/main/mainModel';
import { setIsShowCalculateMetricsModal } from '../../reducers/trainedModel/trainedModelReducer';
import { useNavigate, useParams } from 'react-router-dom';


const Model = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const isLoading = useSelector(getIsLoadingOpenedModel);
  const model = useSelector(getOpenedModel);
  const isModelRemoved = useSelector(getIsModelWasRemoved);
  const duplicatedTo = useSelector(getIsModelDuplicatedTo);
  const navigate = useNavigate();
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState<boolean>(false)
  const [isForceRetrain, setIsForceRetrain] = React.useState<boolean>(false)
  const [isRemoving, setIsRemoving] = React.useState<boolean>(false)
  const [tabValue, setTabValue] = React.useState<number>(0);
  const { getBadgeColor } = useBadge();
  const { onRetrainModel, isRetrainConfirmOpen, setIsRetrainConfirmOpen, onRecalculateMetrics, isRetrainingInProgress, onCancelTraining, isCancelConfirmOpen, setIsCancelConfirmOpen, isCancellationInProgress } = useModel();
  const guideStep = useSelector(getGuideStep);

  React.useEffect(() => {
    if (params.schemaId && params.modelId) {
      dispatch(loadOpenedModelByName({ name: params.modelId, schema: params.schemaId }));
    }
  }, [dispatch, params.modelId, params.schemaId])

  React.useEffect(() => {
    if (guideStep === GuideStepEnum.OpenCreatedModel) {
      dispatch(setGuideStep(GuideStepEnum.QueueStatus));
    }
  }, [dispatch, guideStep])

  const onCopy = React.useCallback(async () => {
    if (model?.id) {
      navigator.clipboard.writeText(model?.id);
      setIsCopied(true);
    }
  }, [model?.id]);

  const openRun = React.useCallback(() => {
    if (model) {
      dispatch(openRunModelModalAction(model))
    }
  }, [dispatch, model]);

  const handleDuplicateModel = React.useCallback(() => {
    if (model?.id) {
      dispatch(openDuplicateModelModalAction(model.id))
    }
  }, [dispatch, model?.id]);

  const handleONNXModel = React.useCallback(() => {
    if (model?.id) {
      dispatch(openOnnxModalAction(model.id))
    }
  }, [dispatch, model?.id]);

  const handleEditModel = React.useCallback(() => {
    if (model) {
      dispatch(editTrainedModelModalAction(model))
    }
  }, [dispatch, model]);

  const handleRemoveModel = React.useCallback(() => {
    if (model?.id) {
      setIsRemoving(true);
      dispatch(removeTrainedModelAction(model.id))
    }
  }, [dispatch, model?.id]);

  React.useEffect(() => {
    if (isModelRemoved) {
      dispatch(setIsModelWasRemoved(false))
      setIsRemoving(false);
      navigate(`/${model?.schema.name}`);
    }
  }, [dispatch, isModelRemoved, model?.schema.name, navigate])

  React.useEffect(() => {
    if (duplicatedTo) {
      dispatch(setIsModelDuplicatedTo(undefined))
      navigate(`/${model?.schema.name}/${duplicatedTo}`);
    }
  }, [dispatch, duplicatedTo, model?.schema.name, navigate])

  const handleTabChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  if (isLoading || !model) {
    return <ModalShimmer />
  }

  const isModelInQueueOrNotTrained = model.queueStateId !== AITrainModelQueueStateTypeEnum.NotInQueue || model.modelStateId !== AITrainModelStateTypeEnum.Trained;

  const isCancellationRequested = model.queueStateId === AITrainModelQueueStateTypeEnum.WaitForCancellation;
  const isCancellable = model.queueStateId !== AITrainModelQueueStateTypeEnum.NotInQueue && !isCancellationRequested;

  const typeColor = getBadgeColor(model!.modelTypeId);
  const stateColor = getBadgeColor(model!.modelStateId);
  const queueColor = getBadgeColor(model!.queueStateId);

  return <Box sx={{ padding: "15px", width: "100%", height: "100%" }}>
    <ConfirmDialog isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} title='Remove model' description='Removing needs to be confirmed' onConfirmedAction={handleRemoveModel} isConfirmDisabled={isRemoving} isProcessing={isRemoving} />
    <ConfirmDialog isOpen={isRetrainConfirmOpen} onClose={() => setIsRetrainConfirmOpen(false)} title='Retrain' onConfirmedAction={() => onRetrainModel(model?.id, isForceRetrain)} isConfirmDisabled={isRetrainingInProgress} isProcessing={isRetrainingInProgress}>
      <FormControlLabel sx={{ margin: 0 }} control={<Checkbox sx={{ padding: 0, marginRight: "10px" }} />} label={<Box>
        <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Force retrain</Typography>
        <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary }}>When enabled, a new model will be created and trained on all the data</Typography>
      </Box>
      } onChange={(_, v) => setIsForceRetrain(v)}
        value={isForceRetrain} />

    </ConfirmDialog>

    <ConfirmDialog isOpen={isCancelConfirmOpen} onClose={() => setIsCancelConfirmOpen(false)} title='Cancel training' description='The current run will be stopped and the model will leave the queue. Already trained models keep their previous version.' onConfirmedAction={() => onCancelTraining(model?.id)} isConfirmDisabled={isCancellationInProgress} isProcessing={isCancellationInProgress} />

    <Box sx={{ marginBottom: "10px" }}>
      <Box sx={{ ...FlexBoxInline, gap: "10px" }}>
        <Typography sx={PageHeaderSx}>
          {model?.name}
        </Typography>
        <Box sx={{ ...FlexBoxInline, gap: "5px" }}>
          <Tooltip placement="bottom" open={guideStep === GuideStepEnum.RunBtn} slotProps={TooltipSlotProps} title={
            <GuideCallout>
              Once your model is trained, click Run to make predictions using new input data.
            </GuideCallout>
          } arrow>
            <IconButton disabled={model?.modelStateId !== AITrainModelStateTypeEnum.Trained} type="button" sx={{ ...PrimaryBtnSx, p: "5px", border: "none" }} aria-label="Run" onClick={openRun}>
              <PlayArrowIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Edit"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="Edit" onClick={handleEditModel}>
              <EditIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Duplicate"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="Duplicate" onClick={handleDuplicateModel}>
              <ContentCopyIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>
          {model?.modelTypeId === ModelType.Onnx && <Tooltip title={"Upload file"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="Duplicate" onClick={handleONNXModel}>
              <UploadFileIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>}
          <Tooltip title={"Remove"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...RemoveBtnSx, p: "5px", border: "none" }} aria-label="Remove" onClick={() => setIsConfirmOpen(true)}>
              <DeleteIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>
        </Box>

      </Box>

      {model?.description && <Typography sx={PageDescriptionSx}>
        {model?.description}
      </Typography>}
      <Box sx={{ ...FlexBoxInline, marginBottom: "10px" }}>
        <Typography sx={{ ...PageDescriptionSx, margin: 0 }}>Id:</Typography>
        <Tooltip title={isCopied ? "Copied!" : "Copy"} placement="right" arrow>
          <Button sx={{ ...ThirdBtnSx }} onClick={onCopy}>{model?.id}</Button>
        </Tooltip>
      </Box>
    </Box>

    <Box sx={{ ...FlexBoxInline, gap: "5px", marginBottom: "20px" }}>
      <Box sx={{ ...BadgeBoxSx, background: typeColor.backgroundColor }}>
        <Typography sx={{ fontSize: Sizes.textSecondary, color: typeColor.textColor }}>{model.modelTypeId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
      </Box>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.TrainedModelStatus} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          This badge shows the current training state of your model (e.g. Not Trained, Training, Trained). Once the model is trained, you can run predictions and view metrics.
          <br />
          <Button sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }} onClick={() => dispatch(setGuideStep(GuideStepEnum.RetrainBtn))}>Next</Button>
        </GuideCallout>
      } arrow>
        <Box sx={{ ...BadgeBoxSx, background: stateColor.backgroundColor }}>
          <Typography sx={{ fontSize: Sizes.textSecondary, color: stateColor.textColor }}>{model.modelStateId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
        </Box>
      </Tooltip>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.QueueStatus} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          This badge shows the queue status of your model (e.g. Not In Queue, In Queue, Processing). It indicates whether your model is waiting to be trained or is currently being processed.
          <br />
          <Button sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }} onClick={() => dispatch(setGuideStep(GuideStepEnum.TrainedModelStatus))}>Next</Button>
        </GuideCallout>
      } arrow>
        <Box sx={{ ...BadgeBoxSx, background: queueColor.backgroundColor }}>
          <Typography sx={{ fontSize: Sizes.textSecondary, color: queueColor.textColor }}>{model.queueStateId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
        </Box>
      </Tooltip>
      <Tooltip title={"Refresh"} placement="bottom" arrow>
        <IconButton type="button" sx={{ ...PrimaryBtnSx, p: "5px", border: "none" }} aria-label="Refresh" onClick={() => dispatch(loadOpenedModel(model?.id))}>
          <RestartAltIcon sx={{ fontSize: Sizes.icon }} />
        </IconButton>
      </Tooltip>
    </Box>

    <Box sx={{ ...FlexBoxInline, gap: "5px", marginBottom: "20px" }}>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.RetrainBtn} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          Click Retrain to start training (or re-training) your model with the current dataset. You can also force a full retrain if needed.
          <br />
          <Button sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }} onClick={() => dispatch(setGuideStep(GuideStepEnum.RecalculateBtn))}>Next</Button>
        </GuideCallout>
      } arrow>
        <Button disabled={model.queueStateId !== AITrainModelQueueStateTypeEnum.NotInQueue} sx={PrimaryBtnSx} onClick={() => setIsRetrainConfirmOpen(true)} startIcon={<ModelTrainingIcon />}>Retrain</Button>
      </Tooltip>
      <Tooltip title={isCancellationRequested ? "Cancellation already requested" : ""} placement="bottom" arrow>
        <span>
          <Button disabled={!isCancellable} sx={RemoveBtnSx} onClick={() => setIsCancelConfirmOpen(true)} startIcon={<CancelIcon />}>
            {isCancellationRequested ? "Cancelling…" : "Cancel"}
          </Button>
        </span>
      </Tooltip>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.RecalculateBtn} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          Click to recalculate model metrics using the current test dataset. This is useful after adding new data without retraining.
          <br />
          <Button sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }} onClick={() => dispatch(setGuideStep(GuideStepEnum.LogsTab))}>Next</Button>
        </GuideCallout>
      } arrow>
        <Button disabled={isModelInQueueOrNotTrained} sx={PrimaryBtnSx} onClick={() => onRecalculateMetrics(model?.id)} startIcon={<SignalCellularAltIcon />}>Recalculate metrics</Button>
      </Tooltip>
      <Button 
        disabled={isModelInQueueOrNotTrained} 
        sx={PrimaryBtnSx} 
        onClick={() => dispatch(setIsShowCalculateMetricsModal(true))} 
        startIcon={<CalculateIcon />}
      >
        Calculate metrics
      </Button>
    </Box>

    <Tabs value={tabValue} onChange={handleTabChange}>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.LogsTab} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          The Logs tab shows the training history and events for your model, including when it was trained and any errors encountered.
          <br />
          <Button sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }} onClick={() => dispatch(setGuideStep(GuideStepEnum.MetricsTab))}>Next</Button>
        </GuideCallout>
      } arrow>
        <Tab sx={TabSx} label="Logs" />
      </Tooltip>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.MetricsTab} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          The Metrics tab displays performance metrics (e.g. accuracy, precision, recall) calculated on the test dataset after training.
          <br />
          <Button sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }} onClick={() => dispatch(setGuideStep(GuideStepEnum.RunBtn))}>Next</Button>
        </GuideCallout>
      } arrow>
        <Tab sx={TabSx} label="Metrics" />
      </Tooltip>
      <Tab sx={TabSx} label="Monitoring" />
    </Tabs>
    <Divider sx={{ marginBottom: "20px" }} />

    <TabsContent modelId={model!.id} tabId={tabValue} modelHistoryLogs={model!.modelHistoryLogs} metrics={model!.metrics} featureMetrics={model!.feature_Metrics} />
  </Box>
}

export default Model;
